import { useState, useEffect, useCallback, useMemo } from "react";
import { Plus, Trash2, Save, Search } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../../lib/api";
import { Field } from "./ImageField";

export const HousesEditor = () => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await adminApi.get("/houses"); setItems(data); } catch { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = (id, key, val) => setItems((p) => p.map((h) => (h.id === id ? { ...h, [key]: val } : h)));

  const add = async () => {
    try {
      const { data } = await adminApi.post("/houses", { house_id: "", coords: "" });
      setItems((p) => [data, ...p]); setQ("");
      toast.success("House added — fill in its ID & coords, then Save");
    } catch { toast.error("Failed to add"); }
  };
  const save = async (h) => {
    try { await adminApi.put(`/houses/${h.id}`, h); toast.success(`House ${h.house_id || ""} saved`); }
    catch { toast.error("Save failed"); }
  };
  const remove = async (id) => {
    try { await adminApi.delete(`/houses/${id}`); setItems((p) => p.filter((h) => h.id !== id)); }
    catch { toast.error("Delete failed"); }
  };

  const filtered = useMemo(
    () => items.filter((h) => String(h.house_id).toLowerCase().includes(q.toLowerCase())),
    [items, q]
  );
  const missing = items.filter((h) => !h.coords).length;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <p className="text-[#8F877C] text-sm">Edit house coordinates or add new houses. Staff see these (searchable, copy-coords) on the Staff panel.{missing > 0 ? ` ${missing} house(s) still need coords.` : ""}</p>
        <button onClick={add} data-testid="house-add-btn" className="inline-flex shrink-0 items-center gap-2 font-feature text-xs uppercase tracking-widest bg-teal text-obsidian px-4 py-2.5 hover:bg-bone transition-colors"><Plus className="h-4 w-4" /> Add House</button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5c554c]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${items.length} houses by ID…`} data-testid="house-editor-search"
          className="w-full bg-obsidian border border-[#2A201A] pl-10 pr-4 py-2.5 text-sm text-bone focus:outline-none focus:border-teal" />
      </div>

      <div className="grid gap-2 sm:grid-cols-2 max-h-[640px] overflow-y-auto pr-1">
        {filtered.map((h) => (
          <div key={h.id} data-testid={`house-edit-${h.id}`} className={`border p-3 space-y-2 ${h.coords ? "border-[#2A201A] bg-surface/40" : "border-gold/40 bg-surface/40"}`}>
            <div className="grid grid-cols-[110px_1fr] gap-2">
              <Field label="House ID" value={h.house_id} onChange={(v) => patch(h.id, "house_id", v)} />
              <Field label="Coordinates" value={h.coords} onChange={(v) => patch(h.id, "coords", v)} placeholder="-1234.5, 678.9, 42.1" />
            </div>
            <div className="flex items-center gap-2">
              {!h.coords && <span className="font-mono text-[10px] uppercase tracking-wider text-gold">needs coords</span>}
              <button onClick={() => save(h)} className="ml-auto inline-flex items-center gap-2 font-feature text-xs uppercase tracking-widest bg-teal/90 text-obsidian px-4 py-1.5 hover:bg-teal"><Save className="h-3.5 w-3.5" /> Save</button>
              <button onClick={() => remove(h.id)} className="p-2 border border-[#2A201A] text-[#8F877C] hover:border-destructive hover:text-destructive transition-colors"><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="font-mono text-xs uppercase tracking-widest text-[#5c554c] py-6">No houses yet. Click “Add House”.</p>}
        {items.length > 0 && filtered.length === 0 && <p className="font-mono text-xs text-[#5c554c] py-4">No matches.</p>}
      </div>
    </div>
  );
};
